import React, { useState, useCallback, useMemo, useEffect } from "react";
import { specialAwardsAction } from "../../shared/utils/googleSheetsApi";
import {Typography } from "@mui/material";
import "./Sidebar.css";
import "./SpecialAwardsSidebar.css";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Check from "@mui/icons-material/Check";

// localStorage key — scoped so it doesn't clash with other app data
const STORAGE_KEY = "ossdf_special_awards";

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { nominations: [], nextTempId: 1 };
    const parsed = JSON.parse(raw);
    // Collapse all open cards on restore so the list is tidy after a refresh
    const nominations = (parsed.nominations || []).map(n => ({ ...n, open: false }));
    return { nominations, nextTempId: parsed.nextTempId || 1 };
  } catch {
    return { nominations: [], nextTempId: 1 };
  }
}

function saveToStorage(nominations, nextTempId) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ nominations, nextTempId }));
  } catch {
    // localStorage unavailable (e.g. private browsing quota) — fail silently
  }
}

// SpecialAwardsSidebar: allows judges to nominate entries for special awards,
// add private notes, assign an award name, and submit when ready.
// Submitted nominations move to a read-only "Submitted" section below.
// Nominations persist across page refreshes via localStorage.
function SpecialAwardsSidebar({ scoreData, rowIndex, colIndexes,
  user,      // ← needed for user.email
  API_BASE,  // ← needed for fetch calls
  onClose,   // optional fn to close the sidebar (e.g. when clicking the header)
}) {
  const stored = loadFromStorage();
  const [nominations, setNominations] = useState(stored.nominations);
  const [nextTempId, setNextTempId] = useState(stored.nextTempId);
  const { entryIdx, titleIdx, levelIdx, styleIdx, sizeIdx, schoolIdx } = colIndexes;
  const userName = user?.name.substring(0, user.name.indexOf(" ")) || "Unknown User";

  // Persist to localStorage whenever nominations or nextTempId change
  useEffect(() => {
    saveToStorage(nominations, nextTempId);
  }, [nominations, nextTempId]);

  // ── Derive current entry details from scoreData + rowIndex ────────────────
  const currentRow = scoreData[rowIndex] || [];

  // Entry number is always column 0, matching CurrentCategorySidebar.
  // Coerce to string — scoreData values from the API/sheet arrive as strings,
  // and we need consistent types for the alreadyAdded comparison below.
  const currentEntryNum = currentRow[entryIdx] || "";

  // Title column — first try colIndexes.title, then search the header by name
  const currentEntryName = currentRow[titleIdx] || "";
  // Level and style for display in the nomination card subtitle
  const currentLevel = currentRow[levelIdx] || "";
  const currentStyle = currentRow[styleIdx] || "";
  const currentSize = currentRow[sizeIdx] || "";
  const currentSchool = currentRow[schoolIdx] || "";

  // Human-readable subtitle shown on the nomination card
  const currentSubtitle = useMemo(() => {
    const level = String(currentLevel || "").trim();
    const firstThree = level.slice(0, 3);
    const isNov = firstThree.toLowerCase() === "nov";

    const shortLevel = isNov
      ? firstThree
      : `${firstThree}${level.length >= 2 ? level.slice(-2) : ""}`;

    return [shortLevel, currentStyle, currentSize].filter(Boolean).join(" · ");
  }, [currentLevel, currentStyle, currentSize]);

  // ── Toggle expand/collapse of a card ─────────────────────────────────────
  const handleToggle = useCallback((id) => {
    setNominations(prev =>
      prev.map(n => n._id === id ? { ...n, open: !n.open } : n)
    );
  }, []);

  // ── Update a field on a nomination ───────────────────────────────────────
  const handleField = useCallback((id, field, value) => {
    setNominations(prev =>
      prev.map(n => n._id === id ? { ...n, [field]: value } : n)
    );
  }, []);



  // Special Awards handlers — each fires a single backend call on user action.
  // All other state (notes, award name edits) is persisted locally in the
  // SpecialAwardsSidebar component via localStorage and never sent to the backend
  // until one of these three explicit actions is triggered.

  const postToBackend = useCallback((body) => {
    if (!userName || !API_BASE) return;
    specialAwardsAction(API_BASE, { email: userName, ...body })
      .catch(e => console.error("[SpecialAwardsSidebar] backend error:", e));
  }, [userName, API_BASE]);

  // ── Add current on-stage entry as a draft nomination ──────────────────────
  const handleAddCurrent = useCallback(() => {
    if (!currentEntryNum || !userName) return;
    const alreadyExists = nominations.some(n => n.entryNum === Number(currentEntryNum));
    if (alreadyExists) return;
    const newNom = {
      _id: nextTempId,
      entryNum: Number(currentEntryNum),
      entryName: currentEntryName,
      subtitle: currentSubtitle,
      school: currentSchool,
      awardName: "",
      notes: "",
      submitted: false,
      open: true,
    };
    setNominations(prev => [...prev, newNom]);
    setNextTempId(n => n + 1);

    postToBackend({
      action: "nominate",
      entryNum: currentEntryNum,
      row: [userName, Number(currentEntryNum), currentEntryName, currentSchool, "", "", "Nominated"],
    });
  }, [currentEntryNum, currentEntryName, currentSchool, currentSubtitle, userName, nominations, nextTempId, postToBackend]);


  // ── Submit a nomination — moves it to the submitted zone ─────────────────
  const handleSubmit = useCallback((id) => {
    setNominations(prev =>
      prev.map(n => {
        if (n._id !== id) return n;
        if (!n.awardName.trim()) return n; // guard: award name required
        postToBackend({
          action: "submit",
          entryNum: n.entryNum,
          row: [userName, n.entryNum, n.entryName, n.school, n.awardName.trim(), n.notes, "Submitted"],
        });
        return { ...n, submitted: true, open: false };
      })
    );
  }, [userName, postToBackend]);

  // ── Recall a submitted nomination back to active ──────────────────────────
  const handleRecall = useCallback((id) => {
    setNominations(prev =>
      prev.map(n => {
        if (n._id !== id) return n;
        postToBackend({
          action: "recall",
          entryNum: n.entryNum,
          row: [userName, n.entryNum, n.entryName, n.school, n.awardName.trim(), n.notes, "Recalled"],
        });
        return { ...n, submitted: false, open: false };
      })
    );
  }, [postToBackend, userName]);

  // ── Remove a draft nomination entirely ───────────────────────────────────
  const handleRemove = useCallback((id) => {
    const nom = nominations.find(n => n._id === id);
    setNominations(prev => prev.filter(n => n._id !== id));
    postToBackend({
      action: "remove",
      entryNum: nom?.entryNum,
    });
  }, [nominations, postToBackend]);

  // --- Clear all nominations (both active and submitted from current localStorage - does not affect backend sheet) ───────────────────────
  const handleClearAll = useCallback(() => {
    setNominations([]);
    localStorage.removeItem(STORAGE_KEY);
    // This ensures all submitted awards stay submitted in the backend sheet, but allows judges to start fresh on the frontend without old nominations cluttering the sidebar. If you want to also clear submitted awards from the backend sheet, you could uncomment this postToBackend call which would trigger a backend route to delete all awards for this user/email:
    // postToBackend({
    //   action: "clearAll",
    // });
  }, []);


  const activeNominations = nominations.filter(n => !n.submitted);
  const submittedNominations = nominations.filter(n => n.submitted);
  const alreadyAdded = currentEntryNum !== null &&
    nominations.some(n => String(n.entryNum) === currentEntryNum);



  return (
    <div className="ossdf-sidebar">

      {/* ── Header ── */}
      <Typography variant="h1" color="primary" component="div" className="ossdf-awards-header">
        <span
          className={`ossdf-awards-title${onClose ? " ossdf-awards-title--clickable" : ""}`}
          onClick={onClose || undefined}
          title={onClose ? "Click to close" : undefined}
          role={onClose ? "button" : undefined}
          aria-label={onClose ? "Close Special Awards" : undefined}
        >
          {onClose && <span className="ossdf-awards-title-arrow">›</span>}
          Special Awards
        </span>
        <button
          className={`ossdf-awards-add-btn${alreadyAdded ? " ossdf-awards-add-btn--added" : ""}`}
          onClick={handleAddCurrent}
          disabled={currentEntryNum === null || alreadyAdded}
          title={alreadyAdded ? "Already nominated" : `Nominate entry #${currentEntryNum}`}
        >
          {alreadyAdded ? `#${currentEntryNum} Added` : `+ Add #${currentEntryNum ?? "—"}`}
        </button>
        <button
          className={`ossdf-awards-clear-btn${activeNominations.length === 0 && submittedNominations.length === 0 ? " ossdf-awards-clear-btn--added" : ""}`}
          onClick={handleClearAll}
          disabled={currentEntryNum === null || (activeNominations.length === 0 && submittedNominations.length === 0)}
          title={"New Session"}
        >
          {"New Session"}
        </button>
      </Typography>

      {/* ── Active nominations list ── */}
      <div className="ossdf-awards-list">
        <div className="ossdf-awards-zone-divider">
          <span className="ossdf-awards-zone-line" />
          <span className="ossdf-awards-zone-label">Nominations</span>
          <span className="ossdf-awards-zone-line" />
        </div>
        {activeNominations.length === 0 && (
          <p className="ossdf-awards-empty">
            No nominations yet. Tap "+ Add" to nominate the current entry.
          </p>
        )}

        {activeNominations.map(nom => (
          <NominationCard
            key={nom._id}
            nom={nom}
            onToggle={handleToggle}
            onField={handleField}
            onSubmit={handleSubmit}
            onRemove={handleRemove}
          />
        ))}
      </div>

      {/* ── Submitted zone ── */}
      {submittedNominations.length > 0 && (
        <div className="ossdf-awards-submitted-zone">
          <div className="ossdf-awards-zone-divider">
            <span className="ossdf-awards-zone-line" />
            <span className="ossdf-awards-zone-label">Submitted</span>
            <span className="ossdf-awards-zone-line" />
          </div>
          {submittedNominations.map(nom => (
            <SubmittedCard
              key={nom._id}
              nom={nom}
              onToggle={handleToggle}
              onRecall={handleRecall}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Active nomination card ────────────────────────────────────────────────
function NominationCard({ nom, onToggle, onField, onSubmit, onRemove }) {
  const hasAward = nom.awardName.trim().length > 0;
  return (
    <div className={`ossdf-awards-card${hasAward ? " ossdf-awards-card--ready" : ""}`}>
      {/* Collapsed header row */}
      <div
        className="ossdf-awards-card-row"
        onClick={() => onToggle(nom._id)}
        role="button"
        aria-expanded={nom.open}
      >
        <div className="ossdf-awards-card-meta">
          <span className="ossdf-awards-entry-line">
            <span className="ossdf-awards-entry-name">#{nom.entryNum} - {nom.entryName}</span>
            <div className="ossdf-awards-card-tools">
              <button
                type="button"
                className="ossdf-awards-icon-btn ossdf-awards-icon-btn--add"
                onClick={(e) => {
                  e.stopPropagation();
                  onSubmit(nom._id);
                }}
                disabled={!hasAward}
                title={!hasAward ? "Enter an award name before submitting" : "Submit nomination"}
                aria-label="Submit nomination"
              >
                <Check fontSize="small" />
              </button>

              <button
                type="button"
                className="ossdf-awards-icon-btn ossdf-awards-icon-btn--remove"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(nom._id);
                }}
                title="Remove nomination"
                aria-label="Remove nomination"
              >
                <DeleteOutlineIcon fontSize="small"/>
              </button>
            </div>
          </span>

          <span className="ossdf-awards-subtitle">{nom.subtitle}</span>
          <span className="ossdf-awards-school">{nom.school}</span>
          <span className={`ossdf-awards-award-name${hasAward ? " ossdf-awards-award-name--set" : ""}`}>
            {hasAward ? nom.awardName : "no award name yet"}
          </span>

        </div>

        {/*<span className={`ossdf-awards-status-dot${hasAward ? " ossdf-awards-status-dot--ready" : ""}`} />*/}
        <span className={`ossdf-awards-chevron${nom.open ? " ossdf-awards-chevron--open" : ""}`}>›</span>

      </div>

      {/* Expanded body */}
      {
        nom.open && (
          <div className="ossdf-awards-card-body">
            <label className="ossdf-awards-field-label">Award name</label>
            <input
              className="ossdf-awards-field-input"
              type="text"
              value={nom.awardName}
              placeholder="e.g. Best Showmanship"
              onChange={e => onField(nom._id, "awardName", e.target.value)}
            />
            <label className="ossdf-awards-field-label ossdf-awards-field-label--gap">Notes</label>
            <textarea
              className="ossdf-awards-field-input ossdf-awards-field-textarea"
              rows={2}
              placeholder="Private notes…"
              value={nom.notes}
              onChange={e => onField(nom._id, "notes", e.target.value)}
            />
            <div className="ossdf-awards-card-actions">
              <button
                className="ossdf-awards-btn ossdf-awards-btn--submit"
                onClick={() => onSubmit(nom._id)}
                disabled={!hasAward}
                title={!hasAward ? "Enter an award name before submitting" : ""}
              >
                Submit
              </button>
              <button
                className="ossdf-awards-btn ossdf-awards-btn--remove"
                onClick={() => onRemove(nom._id)}
              >
                Remove
              </button>
            </div>
          </div>
        )
      }

    </div >);
}

// ─── Submitted (read-only) card ────────────────────────────────────────────
function SubmittedCard({ nom, onToggle, onRecall }) {
  return (
    <div className="ossdf-awards-card ossdf-awards-card--submitted">
      <div
        className="ossdf-awards-card-row"
        onClick={() => onToggle(nom._id)}
        role="button"
        aria-expanded={nom.open}
      >
        <div className="ossdf-awards-card-meta">
          <div className="ossdf-awards-entry-line">
            <span className="ossdf-awards-entry-name ossdf-awards-entry-name--submitted">
              #{nom.entryNum} - {nom.entryName}
            </span>
            <span className="ossdf-awards-submitted-pill">Submitted</span>
          </div>
          <span className="ossdf-awards-subtitle ossdf-awards-subtitle--submitted">{nom.subtitle}</span>
          <span className="ossdf-awards-school ossdf-awards-school--submitted">{nom.school}</span>
          <span className="ossdf-awards-award-name ossdf-awards-award-name--set">{nom.awardName}</span>
        </div>
        <span className={`ossdf-awards-chevron${nom.open ? " ossdf-awards-chevron--open" : ""}`}>›</span>
      </div>

      {nom.open && (
        <div className="ossdf-awards-card-body ossdf-awards-card-body--submitted">
          <label className="ossdf-awards-field-label">Award name</label>
          <input
            className="ossdf-awards-field-input"
            type="text"
            value={nom.awardName}
            readOnly
          />
          {nom.notes.trim() && (
            <>
              <label className="ossdf-awards-field-label ossdf-awards-field-label--gap">Notes</label>
              <textarea
                className="ossdf-awards-field-input ossdf-awards-field-textarea"
                rows={2}
                value={nom.notes}
                readOnly
              />
            </>
          )}
          <div className="ossdf-awards-card-actions">
            <button
              className="ossdf-awards-btn ossdf-awards-btn--recall"
              onClick={() => onRecall(nom._id)}
            >
              ↩ Recall
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SpecialAwardsSidebar;